import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { AppState, Deck, Card, Session } from '../types';
import { createStarterCards } from '../data/starterCards';
import { getDatabaseService } from '../services/DatabaseFactory';

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  loadDecks: () => Promise<void>;
  loadDecksWithCardCounts: () => Promise<void>;
  loadDeck: (id: string) => Promise<Deck | null>;
  loadCards: (deckId: string) => Promise<void>;
  createDeck: (deck: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'>) => Promise<Deck>;
  updateDeck: (id: string, deck: Partial<Deck>) => Promise<Deck>;
  deleteDeck: (id: string) => Promise<void>;
  createCard: (card: Omit<Card, 'id' | 'createdAt' | 'modifiedAt'>) => Promise<Card>;
  updateCard: (id: string, card: Partial<Card>) => Promise<Card>;
  deleteCard: (id: string) => Promise<void>;
  toggleTheme: () => void;
}

type AppAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DECKS'; payload: Deck[] }
  | { type: 'SET_CURRENT_DECK'; payload: Deck | null }
  | { type: 'SET_CARDS'; payload: Card[] }
  | { type: 'SET_CURRENT_CARD'; payload: Card | null }
  | { type: 'SET_SESSIONS'; payload: Session[] }
  | { type: 'SET_CURRENT_SESSION'; payload: Session | null }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'ADD_DECK'; payload: Deck }
  | { type: 'UPDATE_DECK'; payload: Deck }
  | { type: 'REMOVE_DECK'; payload: string }
  | { type: 'ADD_CARD'; payload: Card }
  | { type: 'UPDATE_CARD'; payload: Card }
  | { type: 'REMOVE_CARD'; payload: string };

const initialState: AppState = {
  decks: [],
  currentDeck: null,
  cards: [],
  currentCard: null,
  sessions: [],
  currentSession: null,
  theme: 'light',
  loading: false,
  error: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_DECKS':
      return { ...state, decks: action.payload, loading: false, error: null };
    case 'SET_CURRENT_DECK':
      return { ...state, currentDeck: action.payload };
    case 'SET_CARDS':
      return { ...state, cards: action.payload, loading: false };
    case 'SET_CURRENT_CARD':
      return { ...state, currentCard: action.payload };
    case 'SET_SESSIONS':
      return { ...state, sessions: action.payload };
    case 'SET_CURRENT_SESSION':
      return { ...state, currentSession: action.payload };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'ADD_DECK':
      return { ...state, decks: [action.payload, ...state.decks] };
    case 'UPDATE_DECK':
      return {
        ...state,
        decks: state.decks.map(deck => 
          deck.id === action.payload.id ? action.payload : deck
        ),
        currentDeck: state.currentDeck?.id === action.payload.id ? action.payload : state.currentDeck
      };
    case 'REMOVE_DECK':
      return {
        ...state,
        decks: state.decks.filter(deck => deck.id !== action.payload),
        currentDeck: state.currentDeck?.id === action.payload ? null : state.currentDeck
      };
    case 'ADD_CARD':
      return { ...state, cards: [...state.cards, action.payload] };
    case 'UPDATE_CARD':
      return {
        ...state,
        cards: state.cards.map(card => 
          card.id === action.payload.id ? action.payload : card
        ),
        currentCard: state.currentCard?.id === action.payload.id ? action.payload : state.currentCard
      };
    case 'REMOVE_CARD':
      return {
        ...state,
        cards: state.cards.filter(card => card.id !== action.payload),
        currentCard: state.currentCard?.id === action.payload ? null : state.currentCard
      };
    default:
      return state;
  }
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load initial data and set up menu event handlers
  useEffect(() => {
    const initializeApp = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Load decks using database adapter
        await loadDecks();

        // Load saved theme
        const savedTheme = localStorage.getItem('flashe-theme') as 'light' | 'dark' || 'light';
        dispatch({ type: 'SET_THEME', payload: savedTheme });
        document.documentElement.setAttribute('data-theme', savedTheme);

        // Set up menu event handlers (only for Electron)
        let exportCleanup: (() => void) | undefined;
        if (window.electronAPI) {
          exportCleanup = window.electronAPI.onMenuEvent('menu-export-data', handleExportData);
        }

        // Clear loading state
        dispatch({ type: 'SET_LOADING', payload: false });

        // Cleanup function
        return () => {
          if (exportCleanup) {
            exportCleanup();
          }
        };
      } catch (error) {
        console.error('Failed to initialize app:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize application' });
      }
    };

    const cleanup = initializeApp();
    return () => {
      cleanup?.then(cleanupFn => cleanupFn?.());
    };
  }, []);

  const loadDecks = useCallback(async () => {
    try {
      const db = await getDatabaseService();
      const decks = await db.getDecks();
      const validDecks = Array.isArray(decks) ? decks : [];
      dispatch({ type: 'SET_DECKS', payload: validDecks });
    } catch (error) {
      console.error('Error loading decks:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load decks' });
      // Don't re-throw the error in initialization
    }
  }, []);

  const loadDecksWithCardCounts = useCallback(async () => {
    try {
      const db = await getDatabaseService();
      const decksWithCounts = await db.getDecksWithCardCounts();
      const validDecks = Array.isArray(decksWithCounts) ? decksWithCounts : [];
      dispatch({ type: 'SET_DECKS', payload: validDecks });
    } catch (error) {
      console.error('Error loading decks with card counts:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load decks' });
    }
  }, []);

  const loadDeck = useCallback(async (id: string) => {
    try {
      const db = await getDatabaseService();
      const deck = await db.getDeck(id);
      dispatch({ type: 'SET_CURRENT_DECK', payload: deck });
      return deck;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load deck' });
      throw error;
    }
  }, []);

  const loadCards = useCallback(async (deckId: string) => {
    try {
      const db = await getDatabaseService();
      const cards = await db.getCards(deckId);
      dispatch({ type: 'SET_CARDS', payload: cards });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load cards' });
      throw error;
    }
  }, []);

  const createDeck = useCallback(async (deckData: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'>): Promise<Deck> => {
    try {
      const db = await getDatabaseService();
      const now = new Date().toISOString();
      const deck = await db.createDeck({
        ...deckData,
        createdAt: now,
        modifiedAt: now
      });
      dispatch({ type: 'ADD_DECK', payload: deck });
      return deck;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create deck' });
      throw error;
    }
  }, []);

  const updateDeck = useCallback(async (id: string, deckData: Partial<Deck>): Promise<Deck> => {
    try {
      const db = await getDatabaseService();
      const updatedDeck = await db.updateDeck(id, {
        ...deckData,
        modifiedAt: new Date().toISOString()
      });
      dispatch({ type: 'UPDATE_DECK', payload: updatedDeck });
      return updatedDeck;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update deck' });
      throw error;
    }
  }, []);

  const deleteDeck = useCallback(async (id: string): Promise<void> => {
    try {
      const db = await getDatabaseService();
      await db.deleteDeck(id);
      dispatch({ type: 'REMOVE_DECK', payload: id });
      localStorage.removeItem(`flashe-deck-lastcard-${id}`);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete deck' });
      throw error;
    }
  }, []);;

  const createCard = useCallback(async (cardData: Omit<Card, 'id' | 'createdAt' | 'modifiedAt'>): Promise<Card> => {
    try {
      const db = await getDatabaseService();
      const now = new Date().toISOString();
      const card = await db.createCard({
        ...cardData,
        id: generateId(),
        createdAt: now,
        modifiedAt: now
      });
      dispatch({ type: 'ADD_CARD', payload: card });
      return card;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create card' });
      throw error;
    }
  }, []);

  const updateCard = useCallback(async (id: string, cardData: Partial<Card>): Promise<Card> => {
    try {
      const db = await getDatabaseService();
      const updatedCard = await db.updateCard(id, {
        ...cardData,
        modifiedAt: new Date().toISOString()
      });
      dispatch({ type: 'UPDATE_CARD', payload: updatedCard });
      return updatedCard;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update card' });
      throw error;
    }
  }, []);

  const deleteCard = useCallback(async (id: string): Promise<void> => {
    try {
      const db = await getDatabaseService();
      await db.deleteCard(id);
      dispatch({ type: 'REMOVE_CARD', payload: id });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete card' });
      throw error;
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', payload: newTheme });
    localStorage.setItem('flashe-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  }, [state.theme]);

  const generateId = (): string => {
    return 'id_' + Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
  };

  const handleExportData = async (filePath: string) => {
    try {
      console.log('Frontend: Starting export to:', filePath);
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (!window.electronAPI) {
        throw new Error('Electron API not available');
      }
      
      console.log('Frontend: Calling electronAPI.exportData...');
      const result = await window.electronAPI.exportData(filePath);
      console.log('Frontend: Export result:', result);
      
      if (result.success) {
        console.log('Data exported successfully to:', result.filePath);
        console.log('File size:', result.fileSize, 'bytes');
        
        // Show success notification to user
        alert(`Export successful!\nFile saved to: ${result.filePath}\nSize: ${result.fileSize} bytes`);
        
        dispatch({ type: 'SET_ERROR', payload: null });
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Frontend: Export failed:', error);
      const errorMessage = `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      alert(errorMessage);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const contextValue: AppContextType = {
    state,
    dispatch,
    loadDecks,
    loadDecksWithCardCounts,
    loadDeck,
    loadCards,
    createDeck,
    updateDeck,
    deleteDeck,
    createCard,
    updateCard,
    deleteCard,
    toggleTheme,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}